import { forwardRef } from 'react'
import { Label } from '@/components/ui/label'
import {
  TemplateVariableTextEditor,
  type TemplateVariableTextEditorHandle,
} from '@/components/settings/email/template-variable-text-editor'
import { TemplateVariableRichTextEditor } from '@/components/settings/email/template-variable-rich-text-editor'
import type { RichTextVariableToken } from '@/hooks/use-rich-text-editor'
import type { EmailTemplateTextSlot } from '@/gen/cortado/v1/emails_pb'

interface SystemTemplateSlotEditorProps {
  slot: EmailTemplateTextSlot
  value: string
  onChange: (value: string) => void
  onFocus?: () => void
  variableTokens: RichTextVariableToken[]
  /**
   * Whether to show the slot's default value under the editor. The Settings
   * sheet seeds '' meaning "inherit the default", so showing the default is how
   * the author knows what will actually ship. A per-send composer seeds the
   * EFFECTIVE value instead, where repeating the default is noise.
   */
  showDefault?: boolean
  idPrefix?: string
}

/**
 * SystemTemplateSlotEditor renders one system-email text slot with the editor
 * its `richText` capability calls for.
 *
 * `richText` comes from `generatedSystemEmailRichTextSlots` in the backend,
 * derived from the React Email TSX sources — so which slots accept HTML cannot
 * drift from what the templates actually render. Selecting the editor here, in
 * one place, keeps every slot-editing surface honest about that.
 */
export const SystemTemplateSlotEditor = forwardRef<
  TemplateVariableTextEditorHandle,
  SystemTemplateSlotEditorProps
>(function SystemTemplateSlotEditor(
  { slot, value, onChange, onFocus, variableTokens, showDefault = false, idPrefix = 'slot' },
  ref
) {
  const id = `${idPrefix}-${slot.key}`
  // Both editors expose the same imperative handle, so the only thing that
  // varies is which component renders.
  const Editor = slot.richText ? TemplateVariableRichTextEditor : TemplateVariableTextEditor

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{slot.label}</Label>
      <Editor
        ref={ref}
        id={id}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        placeholder={slot.defaultValue}
        variableTokens={variableTokens}
      />
      {slot.description ? (
        <p className="text-xs text-muted-foreground">{slot.description}</p>
      ) : null}
      {showDefault ? (
        <p className="text-xs text-muted-foreground">
          Default: <span className="font-mono whitespace-pre-wrap">{slot.defaultValue}</span>
        </p>
      ) : null}
    </div>
  )
})
